import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { setEditingOrder } from "../ui/uiSlice";

export interface CartModifierOption {
  id: string;
  displayName: string;
  priceCents: number;
  quantity: number;
}

export interface CartModifierGroup {
  mod_id: string;
  displayName: string;
  options: CartModifierOption[];
}

export interface CartLineItem {
  lineId: string; // unique key: menuItemId + serialized modifiers
  menuItemId: string;
  name: string;
  priceCentsPerUnit: number; // base item + selected modifiers (per unit)
  quantity: number; // number of main items
  imageUrl?: string;
  caloriesLabel?: string;
  modifiers?: CartModifierGroup[];
  backendItem: BackendCartItem;
}

export interface CartState {
  items: CartLineItem[];
}

// Backend payload structures
export interface BackendModifierPayload {
  optionGroup: { guid: string; name?: string };
  item: { guid: string; name?: string };
  quantity: number;
  modifiers: BackendModifierPayload[];
}

export interface BackendCartItem {
  itemGroup?: { guid: string } | null;
  item: { guid: string; name?: string };
  appliedDiscounts?: any[];
  quantity: number;
  modifiers: BackendModifierPayload[];
}

const initialState: CartState = {
  items: [],
};

function computeLineId(
  menuItemId: string,
  modifiers?: CartModifierGroup[],
): string {
  const normalized = (modifiers || [])
    .map((g) => ({
      mod_id: g.mod_id,
      options: [...g.options]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((o) => ({ id: o.id, qty: o.quantity })),
    }))
    .sort((a, b) => a.mod_id.localeCompare(b.mod_id));
  const signature = normalized.length > 0 ? JSON.stringify(normalized) : "";
  return `${menuItemId}|${signature}`;
}

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    // Ensure state.items exists (migrates old persisted shape on-the-fly)
    _ensureItemsArray(state) {
      const s: any = state as any;
      if (Array.isArray(s.items)) return;
      const itemsById: Record<string, CartLineItem> = s.itemsById || {};
      s.items = Object.values(itemsById);
      delete s.itemsById;
    },
    addItem(
      state,
      action: PayloadAction<{
        menuItemId: string;
        name: string;
        basePriceCents: number;
        quantity: number;
        imageUrl?: string;
        caloriesLabel?: string;
        modifiers?: CartModifierGroup[];
        backendItem: BackendCartItem;
      }>,
    ) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const {
        menuItemId,
        name,
        basePriceCents,
        quantity,
        imageUrl,
        caloriesLabel,
        modifiers,
        backendItem,
      } = (action as any).payload;
      const unitModifiersCents = (modifiers ?? []).reduce(
        (sum: number, g: CartModifierGroup) =>
          sum +
          (g.options || []).reduce(
            (s: number, o: CartModifierOption) => s + o.priceCents * o.quantity,
            0,
          ),
        0,
      );
      const priceCentsPerUnit = basePriceCents + unitModifiersCents;
      const lineId = computeLineId(menuItemId, modifiers as any);
      const existing = state.items.find((li) => li.lineId === lineId);
      if (existing) {
        (existing as any).quantity += quantity;
        if ((existing as any).backendItem)
          (existing as any).backendItem.quantity += quantity;
      } else {
        state.items.push({
          lineId,
          menuItemId,
          name,
          priceCentsPerUnit,
          imageUrl,
          caloriesLabel,
          quantity,
          modifiers:
            modifiers && (modifiers as any).length
              ? (modifiers as any)
              : undefined,
          backendItem: { ...(backendItem as any) },
        } as any);
      }
    },
    replaceItem(
      state,
      action: PayloadAction<{
        oldLineId: string;
        menuItemId: string;
        name: string;
        basePriceCents: number;
        quantity: number;
        imageUrl?: string;
        caloriesLabel?: string;
        modifiers?: CartModifierGroup[];
        backendItem: BackendCartItem;
      }>,
    ) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === (action as any).payload.oldLineId,
      );
      if (idx === -1) return;
      const {
        menuItemId,
        name,
        basePriceCents,
        quantity,
        imageUrl,
        caloriesLabel,
        modifiers,
        backendItem,
      } = (action as any).payload;
      const unitModifiersCents = (modifiers ?? []).reduce(
        (sum: number, g: CartModifierGroup) =>
          sum +
          (g.options || []).reduce(
            (s: number, o: CartModifierOption) => s + o.priceCents * o.quantity,
            0,
          ),
        0,
      );
      const priceCentsPerUnit = basePriceCents + unitModifiersCents;
      const lineId = computeLineId(menuItemId, modifiers as any);
      (state.items as any)[idx] = {
        lineId,
        menuItemId,
        name,
        priceCentsPerUnit,
        imageUrl,
        caloriesLabel,
        quantity,
        modifiers:
          modifiers && (modifiers as any).length
            ? (modifiers as any)
            : undefined,
        backendItem: { ...(backendItem as any) },
      } as any;
    },
    addOne(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const line = state.items.find(
        (li) => li.lineId === action.payload.lineId,
      );
      if (!line) return;
      (line as any).quantity += 1;
      if ((line as any).backendItem)
        (line as any).backendItem.quantity =
          ((line as any).backendItem.quantity || 0) + 1;
    },
    removeOne(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === action.payload.lineId,
      );
      if (idx === -1) return;
      const line = state.items[idx] as any;
      if (line.quantity <= 1) {
        state.items.splice(idx, 1);
      } else {
        line.quantity -= 1;
        if (line.backendItem)
          line.backendItem.quantity = Math.max(0, (line as any).quantity);
      }
    },
    clearItem(state, action: PayloadAction<{ lineId: string }>) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      const idx = state.items.findIndex(
        (li) => li.lineId === action.payload.lineId,
      );
      if (idx !== -1) (state.items as any).splice(idx, 1);
    },
    clearCart(state) {
      (cartSlice.caseReducers._ensureItemsArray as any)(state);
      state.items = [] as any;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setEditingOrder,
      (state, action: PayloadAction<boolean>) => {
        if (action.payload === false) {
          state.items = [] as any;
        }
      },
    );
  },
});

export const { addItem, addOne, removeOne, clearItem, clearCart, replaceItem } =
  cartSlice.actions;
export default cartSlice.reducer;

export const selectCart = (state: any): CartState => {
  const c: any = state.cart ?? {};
  if (Array.isArray(c.items)) return c as CartState;
  const itemsById: Record<string, CartLineItem> = c.itemsById ?? {};
  return { items: Object.values(itemsById) };
};

export const selectCartItems = createSelector(selectCart, (cart) => cart.items);

export const selectCartCount = createSelector(selectCartItems, (items) =>
  items.reduce((acc, it) => acc + it.quantity, 0),
);

export const selectCartTotalCents = createSelector(selectCartItems, (items) =>
  items.reduce((acc, it) => acc + it.quantity * it.priceCentsPerUnit, 0),
);
